/**
 * Drawing a maze: wall and route paths as SVG path data, in plain user units.
 *
 * A rectangular cell is `CELL` units square. A circular maze has rings `CELL` units
 * thick, the centre cell being a disc of radius `CELL`. Both leave a `MARGIN` around the
 * maze for the start marker.
 */

import {
  type Maze,
  ringOf,
  solution,
} from "@/lib/tool-kit/games-organizers/maze";

export const CELL = 10;
export const MARGIN = 8;
const DECIMALS = 2;
const FULL_TURN = 2 * Math.PI;

export interface MazeDrawing {
  /** The viewBox, `minX minY width height`. */
  viewBox: string;
  width: number;
  height: number;
  walls: string;
  route: string;
  start: { x: number; y: number };
  finish: { x: number; y: number };
}

function num(value: number): string {
  return String(Number(value.toFixed(DECIMALS)));
}

function point(radius: number, angle: number): { x: number; y: number } {
  return { x: radius * Math.cos(angle), y: radius * Math.sin(angle) };
}

/* -------------------------------------------------------------- rectangular */

/** The walls one cell owns: its top and left on the outer edge, its right and bottom always. */
function cellWalls(maze: Maze, cell: number): string[] {
  const { cols, rows } = maze.graph;
  const r = Math.floor(cell / cols);
  const c = cell % cols;
  const x = c * CELL;
  const y = r * CELL;
  const open = (b: number) => maze.links[cell]?.includes(b) ?? false;
  const parts: string[] = [];
  if (r === 0 && cell !== maze.entrance) {
    parts.push(`M${x} ${y}h${CELL}`);
  }
  if (c === 0) {
    parts.push(`M${x} ${y}v${CELL}`);
  }
  if (c === cols - 1 || !open(cell + 1)) {
    parts.push(`M${x + CELL} ${y}v${CELL}`);
  }
  const bottomOpen = r === rows - 1 ? cell === maze.exit : open(cell + cols);
  if (!bottomOpen) {
    parts.push(`M${x} ${y + CELL}h${CELL}`);
  }
  return parts;
}

function rectangularWalls(maze: Maze): string {
  const parts: string[] = [];
  for (let cell = 0; cell < maze.graph.cells; cell += 1) {
    parts.push(...cellWalls(maze, cell));
  }
  return parts.join("");
}

function rectangularDrawing(maze: Maze): MazeDrawing {
  const { cols, rows } = maze.graph;
  const center = (cell: number) => ({
    x: (cell % cols) * CELL + CELL / 2,
    y: Math.floor(cell / cols) * CELL + CELL / 2,
  });
  const path = solution(maze).map(center);
  const first = path[0] ?? { x: 0, y: 0 };
  const last = path.at(-1) ?? first;
  const route = [
    `M${num(first.x)} ${num(first.y - CELL / 2 - MARGIN / 2)}`,
    ...path.map((p) => `L${num(p.x)} ${num(p.y)}`),
    `L${num(last.x)} ${num(last.y + CELL / 2 + MARGIN / 2)}`,
  ].join("");
  const width = cols * CELL;
  const height = rows * CELL;
  return {
    viewBox: `${-MARGIN} ${-MARGIN} ${width + 2 * MARGIN} ${height + 2 * MARGIN}`,
    width: width + 2 * MARGIN,
    height: height + 2 * MARGIN,
    walls: rectangularWalls(maze),
    route,
    start: { x: first.x, y: -MARGIN / 2 },
    finish: { x: last.x, y: height + MARGIN / 2 },
  };
}

/* ----------------------------------------------------------------- circular */

function arc(radius: number, from: number, to: number): string {
  const a = point(radius, from);
  const b = point(radius, to);
  const sweep = to >= from ? 1 : 0;
  const large = Math.abs(to - from) > Math.PI ? 1 : 0;
  return `M${num(a.x)} ${num(a.y)}A${num(radius)} ${num(radius)} 0 ${large} ${sweep} ${num(b.x)} ${num(b.y)}`;
}

function circularWalls(maze: Maze): string {
  const { graph } = maze;
  const parts: string[] = [];
  const outer = graph.ringCounts.length - 1;
  const open = (a: number, b: number) => maze.links[a]?.includes(b) ?? false;
  for (let ring = 1; ring <= outer; ring += 1) {
    const count = graph.ringCounts[ring] ?? 1;
    const start = graph.ringStart[ring] ?? 0;
    const inner = graph.ringCounts[ring - 1] ?? 1;
    const innerStart = graph.ringStart[ring - 1] ?? 0;
    const ratio = count / inner;
    const r0 = ring * CELL;
    const r1 = (ring + 1) * CELL;
    for (let j = 0; j < count; j += 1) {
      const cell = start + j;
      const a0 = (FULL_TURN * j) / count;
      const a1 = (FULL_TURN * (j + 1)) / count;
      if (!open(cell, innerStart + Math.floor(j / ratio))) {
        parts.push(arc(r0, a0, a1));
      }
      if (!open(cell, start + ((j + 1) % count))) {
        const p = point(r0, a1);
        const q = point(r1, a1);
        parts.push(`M${num(p.x)} ${num(p.y)}L${num(q.x)} ${num(q.y)}`);
      }
      if (ring === outer && cell !== maze.entrance) {
        parts.push(arc(r1, a0, a1));
      }
    }
  }
  return parts.join("");
}

interface Polar {
  r: number;
  a: number;
}

function middleOf(maze: Maze, cell: number): Polar {
  const { ring, index } = ringOf(maze.graph, cell);
  if (ring === 0) {
    return { r: 0, a: 0 };
  }
  const count = maze.graph.ringCounts[ring] ?? 1;
  return { r: (ring + 0.5) * CELL, a: (FULL_TURN * (index + 0.5)) / count };
}

/** Shortest signed turn from one angle to another, in (−π, π]. */
function turn(from: number, to: number): number {
  let d = (to - from) % FULL_TURN;
  if (d > Math.PI) {
    d -= FULL_TURN;
  } else if (d <= -Math.PI) {
    d += FULL_TURN;
  }
  return d;
}

/**
 * The route as polar waypoints, each joined to the last either along a ring (same radius)
 * or straight along a radius (same angle), so the line never cuts through a wall. When
 * the route crosses between rings it crosses at the angle of the finer cell.
 */
function routeWaypoints(maze: Maze, cells: readonly number[]): Polar[] {
  const outerRadius = (maze.graph.ringCounts.length + 0.2) * CELL;
  const points: Polar[] = [];
  const first = cells[0];
  if (first === undefined) {
    return points;
  }
  const begin = middleOf(maze, first);
  points.push({ r: outerRadius, a: begin.a }, begin);
  for (let i = 1; i < cells.length; i += 1) {
    const from = middleOf(maze, cells[i - 1] as number);
    const to = middleOf(maze, cells[i] as number);
    if (from.r === to.r) {
      points.push(to);
      continue;
    }
    const finerAngle = from.r > to.r ? from.a : to.a;
    const crossing = to.r === 0 ? from.a : finerAngle;
    points.push({ r: from.r, a: crossing }, { r: to.r, a: crossing }, to);
  }
  return points;
}

function pathThrough(points: readonly Polar[]): string {
  const first = points[0];
  if (!first) {
    return "";
  }
  const start = point(first.r, first.a);
  let d = `M${num(start.x)} ${num(start.y)}`;
  let previous = first;
  for (const next of points.slice(1)) {
    const end = point(next.r, next.a);
    if (next.r === previous.r && next.a !== previous.a && next.r > 0) {
      const delta = turn(previous.a, next.a);
      d += `A${num(next.r)} ${num(next.r)} 0 0 ${delta >= 0 ? 1 : 0} ${num(end.x)} ${num(end.y)}`;
    } else if (next.r !== previous.r || next.a !== previous.a) {
      d += `L${num(end.x)} ${num(end.y)}`;
    }
    previous = next;
  }
  return d;
}

function circularDrawing(maze: Maze): MazeDrawing {
  const rings = maze.graph.ringCounts.length;
  const radius = rings * CELL;
  const route = pathThrough(routeWaypoints(maze, solution(maze)));
  const entrance = middleOf(maze, maze.entrance);
  const size = 2 * (radius + MARGIN);
  return {
    viewBox: `${-(radius + MARGIN)} ${-(radius + MARGIN)} ${size} ${size}`,
    width: size,
    height: size,
    walls: circularWalls(maze),
    route,
    start: point(radius + MARGIN / 2, entrance.a),
    finish: { x: 0, y: 0 },
  };
}

export function drawMaze(maze: Maze): MazeDrawing {
  return maze.graph.shape === "circular"
    ? circularDrawing(maze)
    : rectangularDrawing(maze);
}

/** Stroke width, in user units, that prints as `mm` millimetres on a sheet `pageMm` wide. */
export function strokeFor(
  drawing: MazeDrawing,
  mm = 0.4,
  pageMm = 180
): number {
  return Number(((mm * drawing.width) / pageMm).toFixed(3));
}
